package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminProductCategoryItem
import org.gymly.theme.AdminColors
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.userMessage

@Composable
fun AdminProductCategoriesPanel(onChanged: () -> Unit = {}) {
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf<List<AdminProductCategoryItem>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }
    var editor by remember { mutableStateOf<AdminProductCategoryItem?>(null) }
    var viewing by remember { mutableStateOf<AdminProductCategoryItem?>(null) }
    var creating by remember { mutableStateOf(false) }
    var pendingDelete by remember { mutableStateOf<AdminProductCategoryItem?>(null) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listProductCategories()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar las categorías.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    Column(
        Modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        // Celular (ancho < 460 dp): el botón va debajo del título; en pantallas anchas, a la derecha.
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val stacked = maxWidth < 460.dp
            val titleBlock: @Composable (Modifier) -> Unit = { mod ->
                Column(mod) {
                    Text("Categorías", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
                    Text(
                        "Son las categorías del formulario de productos.",
                        color = AdminColors.TextMuted,
                        fontSize = 13.sp,
                    )
                }
            }
            val addButton: @Composable (Modifier) -> Unit = { mod ->
                Button(
                    onClick = { creating = true },
                    modifier = mod,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Agregar categoría", fontWeight = FontWeight.SemiBold)
                }
            }
            if (stacked) {
                Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    titleBlock(Modifier.fillMaxWidth())
                    addButton(Modifier.fillMaxWidth())
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    titleBlock(Modifier.weight(1f))
                    addButton(Modifier)
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        error?.let {
            Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
        }
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            items.isEmpty() -> Text("No hay categorías todavía.", color = AdminColors.TextMuted)
            else -> VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth()) {
                items.forEach { item ->
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(item.name, color = AdminColors.Text, modifier = Modifier.weight(1f))
                        Text(
                            productCountLabel(item.productCount),
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                            modifier = Modifier.padding(end = 8.dp),
                        )
                        IconButton(onClick = { viewing = item }) {
                            Icon(Icons.Outlined.Search, "Consultar", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { editor = item }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { pendingDelete = item }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
            }
        }
    }

    viewing?.let { item ->
        CategoryDetailDialog(
            item = item,
            onDismiss = { viewing = null },
            onEdit = {
                viewing = null
                editor = item
            },
        )
    }

    if (creating || editor != null) {
        CategoryNameDialog(
            title = if (creating) "Nueva categoría" else "Editar categoría",
            initial = editor?.name.orEmpty(),
            saving = saving,
            onDismiss = {
                if (!saving) {
                    creating = false
                    editor = null
                }
            },
            onSave = { name ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        if (creating) {
                            AdminRepo.api.createProductCategory(name)
                        } else {
                            editor?.let { AdminRepo.api.updateProductCategory(it.id, name) }
                        }
                        creating = false
                        editor = null
                        reload()
                        onChanged()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar la categoría.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
    }

    pendingDelete?.let { item ->
        AlertDialog(
            onDismissRequest = { if (!saving) pendingDelete = null },
            title = { Text("Eliminar categoría") },
            text = { Text("Se quitará «${item.name}». Solo se puede si ningún producto la usa.") },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteProductCategory(item.id)
                                pendingDelete = null
                                reload()
                                onChanged()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar la categoría.")
                                pendingDelete = null
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Error,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) { Text(if (saving) "…" else "Eliminar") }
            },
            dismissButton = {
                TextButton(onClick = { pendingDelete = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
    }
}

private fun productCountLabel(count: Int): String = when (count) {
    0 -> "Sin productos"
    1 -> "1 producto"
    else -> "$count productos"
}

@Composable
private fun CategoryDetailDialog(
    item: AdminProductCategoryItem,
    onDismiss: () -> Unit,
    onEdit: () -> Unit,
) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 420.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Categoría", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            Text(item.name, fontWeight = FontWeight.SemiBold, fontSize = 16.sp, color = AdminColors.Text)
            Spacer(Modifier.height(6.dp))
            Text(productCountLabel(item.productCount), color = AdminColors.TextMuted, fontSize = 13.sp)
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End)) {
                OutlinedButton(onClick = onDismiss, shape = RoundedCornerShape(10.dp)) {
                    Text("Cerrar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = onEdit,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) { Text("Editar", fontWeight = FontWeight.SemiBold) }
            }
        }
    }
}

@Composable
private fun CategoryNameDialog(
    title: String,
    initial: String,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (String) -> Unit,
) {
    var name by remember(initial) { mutableStateOf(initial) }
    var formError by remember { mutableStateOf<String?>(null) }
    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        Column(
            Modifier
                .widthIn(max = 420.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            OutlinedTextField(
                value = name,
                onValueChange = { name = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = { Text("Nombre de la categoría", color = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End)) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        val label = name.trim()
                        if (label.isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        formError = null
                        onSave(label)
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) { Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold) }
            }
        }
    }
}
